import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { showSuccessToast } from '@ui';
import { useDirectUpload } from '@hooks/useDirectUpload';
import { translate } from '@utils/i18n';
import { createDirectUploadTransport } from '../services/directUploadTransport';
import { buildingAttachmentsKeys } from './queryKeys';
import { fileTypeLabel, formatFileSize, splitAttachmentName } from '../utils/attachmentFormatting';
const NS = 'features.building_attachments.upload';
const TYPE_LABEL_NS = 'features.building_attachments.table';
const TECHS_VALUE = 'techs';
const PORTAL_VALUE = 'portal';
/**
 * Bridges `useDirectUpload` (task-08, transport-agnostic, S3/contract-aware) to
 * `AddAttachmentsBrushaway`'s generic queue shape (task-09's organism, storage-agnostic). Owns the
 * hook instance and the drawer's own `isOpen` state -- both live here, one level above the
 * organism, per the per-mount constraint task-22 documents (queue state must survive the drawer
 * closing while a batch keeps running in the background).
 */
export function useAddAttachmentsUpload(buildingId) {
    const queryClient = useQueryClient();
    const transport = useMemo(() => createDirectUploadTransport(buildingId), [buildingId]);
    const upload = useDirectUpload({ transport });
    const [isOpen, setIsOpen] = useState(false);
    const t = useCallback((key) => translate(key, { namespace: TYPE_LABEL_NS }), []);
    // E2E-07: a batch that finishes with every item `'done'` closes the drawer, shows the success
    // toast, and refreshes the table -- distinct from task-22's background-progress toast, which
    // reacts to the drawer already being closed mid-upload, not to a clean foreground completion.
    // No dependency array: this must re-check the running->stopped transition after every render
    // (including ones triggered by `upload.items` progress ticks), not just when a memoized
    // dependency identity changes.
    const wasRunningRef = useRef(false);
    useEffect(() => {
        if (wasRunningRef.current && !upload.isRunning) {
            const allDone = upload.items.length > 0 && upload.items.every((item) => item.status === 'done');
            if (allDone) {
                setIsOpen(false);
                showSuccessToast(translate('toasts.success', { namespace: NS, count: upload.items.length }));
                void queryClient.invalidateQueries({ queryKey: buildingAttachmentsKeys.list(buildingId) });
                upload.reset();
            }
        }
        wasRunningRef.current = upload.isRunning;
    });
    const items = useMemo(() => upload.items.map((item) => {
        var _a, _b, _c;
        const { basename: originalName, extension } = splitAttachmentName(item.file.name);
        const isPrivate = (_a = item.isPrivate) !== null && _a !== void 0 ? _a : false;
        const showOnCustomerPortal = (_b = item.showOnCustomerPortal) !== null && _b !== void 0 ? _b : false;
        return {
            id: item.id,
            originalName,
            name: item.name,
            extension,
            typeLabel: fileTypeLabel(item.file.name, t),
            sizeLabel: formatFileSize(item.file.size),
            byteSize: item.file.size,
            status: item.status,
            progress: item.progress,
            categoryValue: (_c = item.attachmentCategoryId) !== null && _c !== void 0 ? _c : null,
            visibilityValues: [...(!isPrivate ? [TECHS_VALUE] : []), ...(showOnCustomerPortal ? [PORTAL_VALUE] : [])]
        };
    }), [upload.items, t]);
    const openDrawer = useCallback(() => setIsOpen(true), []);
    const onClose = useCallback(() => {
        setIsOpen(false);
        upload.reset();
    }, [upload]);
    const onCloseToBackground = useCallback(() => setIsOpen(false), []);
    const onFilesSelected = useCallback((files) => upload.addFiles(files), [upload]);
    const onNameChange = useCallback((id, nextBasename) => upload.updateItemMetadata(id, { name: nextBasename }), [upload]);
    const onCategoryChange = useCallback(
    // Category values here are always `number | null` (mirroring `CategoryEditOption.id`) --
    // `string` is part of the organism's generic option-value type for other, non-numeric
    // consumers, never actually produced by this feature's own category options.
    (id, value) => upload.updateItemMetadata(id, { attachmentCategoryId: value }), [upload]);
    const onVisibilityChange = useCallback((id, values) => upload.updateItemMetadata(id, {
        isPrivate: !values.includes(TECHS_VALUE),
        showOnCustomerPortal: values.includes(PORTAL_VALUE)
    }), [upload]);
    const onRemoveItem = useCallback((id) => upload.removeItem(id), [upload]);
    const onRetryItem = useCallback((id) => {
        void upload.retry(id);
    }, [upload]);
    const onRetryFailed = useCallback(() => {
        void upload.retryFailed();
    }, [upload]);
    const onCancelUpload = useCallback(() => upload.cancel(), [upload]);
    const onSubmit = useCallback(() => {
        void upload.start();
    }, [upload]);
    return {
        isOpen,
        openDrawer,
        items,
        isUploading: upload.isRunning,
        aggregateProgress: upload.aggregateProgress,
        onFilesSelected,
        onNameChange,
        onCategoryChange,
        onVisibilityChange,
        onRemoveItem,
        onRetryItem,
        onRetryFailed,
        onCancelUpload,
        onSubmit,
        onClose,
        onCloseToBackground
    };
}
